import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Bookmark, Radar, Scale, TrendingUp } from "lucide-react";

import {
  ReachMapAccountHoverCard,
  RevenueMapAccountHoverCard,
  computeMapHoverPosition,
} from "./BirdsEyeMapHoverCards";
import {
  healthDotColorFromOverall,
  nodeFillFromOverall,
} from "./birdsEyeMapStyles";
import CommonModal from "../../../../../Components/Modal";
import TabsComponent from "../../../../../Components/Tab";
import SpiderCompareModalContent from "../../../../../intelligence-briefing-library/components/hawkeye/SpiderCompareModalContent";
import { useAppSelector } from "../../../../../Store/Hooks";
import { DEFAULT_HEALTH_SCORE_THRESHOLDS } from "../../../../../Utils/HealthThresholds";

type ClusterId = "renewal-risk" | "strong-performers" | "high-adoption-mixed";
type Quadrant = "expand" | "triage" | "defend" | "delegate";

export type BoxSelectionPoint = {
  account_id: string;
  account_name: string;
  cluster: ClusterId;
  quadrant: Quadrant;
  overall: number;
  execEngagement: number;
  reachOpp: number;
  reachRisk: number;
  arrM: number;
  nrr: number;
};

export type ReachScatterLayout = {
  rw: number;
  rh: number;
  padL: number;
  padT: number;
  plotW: number;
  plotH: number;
  hx: number;
  hy: number;
  qTopTitleY: number;
  qTopSubY: number;
  qBotTitleY: number;
  qBotSubY: number;
};

export type RevenueScatterLayout = {
  revW: number;
  revH: number;
  padL: number;
  padR: number;
  padT: number;
  padB: number;
  revPlotW: number;
  revPlotH: number;
  arrMin: number;
  arrMax: number;
  arrTicks: number[];
  arrMedianM: number;
  revenueNrrMainTicks: number[];
  revenueYBottom: number;
  revenueYJoin: number;
  revenueYTop: number;
  revenueHLow: number;
  nrrTop: number;
  nrrMainSpan: number;
};

type CompareAccount = {
  account_id: string;
  account_name: string;
  overallScore?: number;
  categoryScores: Array<{
    category: string;
    score: number;
  }>;
};

type BirdsEyeBoxSelectionModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  view: "reach" | "revenue";
  accountIds: string[];
  pointsById: Map<string, BoxSelectionPoint>;
  selectedIds: Set<string>;
  onToggleAccount: (accountId: string) => void;
  execAttnCaution: number;
  execAttnCritical: number;
  reachLayout: ReachScatterLayout;
  revenueLayout: RevenueScatterLayout;
  onSaveWatchlist?: () => void;
  onSwitchMapView?: () => void;
  compareAccounts?: CompareAccount[];
  compareCategoryOrder?: string[];
  compareColors?: string[];
};

const MODAL_OUTLINE_BTN =
  "inline-flex items-center gap-1 rounded-md border border-violet-600 bg-white px-3 py-1.5 text-[12px] font-semibold text-violet-700 transition-colors hover:bg-violet-50 disabled:cursor-not-allowed disabled:opacity-50";

const MAP_NODE_R = 13;
/** Gap between bubble edge and purple dashed selection ring. */
const SELECTION_RING_GAP = 3;
const MAP_COLLISION_BASE_PX = 10;
/** Min center-to-center distance (bubble + health/exec badges + selection ring). */
const MIN_CENTER_DIST = (MAP_NODE_R + 11) * 2;
const PLOT_NODE_MARGIN = MAP_NODE_R + 14;
const MAX_COMPARE_VISIBLE_ACCOUNTS = 4;

const clampNum = (n: number, min: number, max: number) =>
  Math.max(min, Math.min(max, n));

const clamp = (n: number, min = 0, max = 100) =>
  Math.max(min, Math.min(max, n));

const initials = (name: string) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .map(w => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

/** Unique 2–3 char labels for every account in the snippet map (no duplicate "NS"/"NOS"). */
function buildMapNodeLabels(
  accounts: BoxSelectionPoint[]
): Map<string, string> {
  const labels = new Map<string, string>();
  const used = new Set<string>();

  const sorted = [...accounts].sort(
    (a, b) =>
      a.account_name.localeCompare(b.account_name, undefined, {
        sensitivity: "base",
      }) || a.account_id.localeCompare(b.account_id)
  );

  const candidatesFor = (account: BoxSelectionPoint): string[] => {
    const words = account.account_name.split(/\s+/).filter(Boolean);
    const compact = account.account_name.replace(/[^a-zA-Z0-9]/g, "");
    return [
      initials(account.account_name),
      words.length >= 2
        ? `${words[0].slice(0, 2)}${words[1][0]}`.toUpperCase()
        : "",
      words.length >= 2
        ? `${words[0][0] ?? ""}${words[1].slice(0, 2)}`.toUpperCase()
        : "",
      compact.slice(0, 3).toUpperCase(),
      compact.slice(-3).toUpperCase(),
    ].filter(Boolean);
  };

  for (const account of sorted) {
    const candidates = candidatesFor(account);
    let label =
      candidates.find(candidate => !used.has(candidate)) ??
      candidates[0] ??
      "?";
    let suffix = 2;
    const base = label.slice(0, 2) || "A";
    while (used.has(label)) {
      label = `${base}${suffix}`;
      suffix += 1;
    }
    used.add(label);
    labels.set(account.account_id, label.slice(0, 4));
  }

  return labels;
}

type PlotBounds = {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
};

/** Larger plot in the modal → more room for spread bubbles (width + height). */
const MODAL_REACH_HEIGHT_SCALE = 1.48;
const MODAL_REACH_WIDTH_SCALE = 1.32;
const MODAL_REVENUE_HEIGHT_SCALE = 1.28;
const MODAL_REVENUE_WIDTH_SCALE = 1.18;
const REVENUE_NRR_BREAK_FRAC = 0.1;

/** Extra space below plot for x-axis title + Low/High labels in the modal. */
const MODAL_REACH_BOTTOM_PAD = 18;

function scaleReachLayoutForModal(
  layout: ReachScatterLayout
): ReachScatterLayout {
  const padB = layout.rh - layout.padT - layout.plotH + MODAL_REACH_BOTTOM_PAD;
  const padR = layout.rw - layout.padL - layout.plotW;
  const plotH = Math.round(layout.plotH * MODAL_REACH_HEIGHT_SCALE);
  const plotW = Math.round(layout.plotW * MODAL_REACH_WIDTH_SCALE);
  const rh = layout.padT + plotH + padB;
  const rw = layout.padL + plotW + padR;
  const { padL, padT } = layout;
  const hx = padL + plotW / 2;
  const hy = padT + plotH / 2;
  const qLabelInset = 2;
  const qTitleSubGap = 4;
  const qTitleToSub = 9 + qTitleSubGap;
  const qTopTitleY = padT + 13 - qLabelInset;
  const qTopSubY = qTopTitleY + qTitleToSub;
  const qBotSubY = padT + plotH - 5 - qLabelInset;
  const qBotTitleY = qBotSubY - qTitleToSub;
  return {
    rw,
    rh,
    padL,
    padT,
    plotW,
    plotH,
    hx,
    hy,
    qTopTitleY,
    qTopSubY,
    qBotTitleY,
    qBotSubY,
  };
}

const MODAL_REVENUE_BOTTOM_PAD = 16;

type PlotAnchor = { id: string; ax: number; ay: number };

/** Bubble centers closer than this get a small readable gap (slight rim overlap OK). */
const MODAL_TOUCH_DIST = MAP_NODE_R * 2 * 0.96;
const MODAL_TARGET_GAP = MAP_NODE_R * 2 * 0.9;
const MODAL_MAX_DRIFT = MAP_NODE_R * 4.5;
const MODAL_REACH_TOUCH_DIST = MAP_NODE_R * 2 * 1.02;
const MODAL_REACH_TARGET_GAP = MAP_NODE_R * 2 * 0.98;
const MODAL_REACH_MAX_DRIFT = MAP_NODE_R * 5;

type ModalBubbleSpacing = {
  touchDist?: number;
  targetGap?: number;
  maxDrift?: number;
};

class UnionFind {
  parent = new Map<string, string>();
  find(id: string): string {
    let root = id;
    while (this.parent.get(root) !== root) root = this.parent.get(root)!;
    let cur = id;
    while (cur !== root) {
      const next = this.parent.get(cur)!;
      this.parent.set(cur, root);
      cur = next;
    }
    return root;
  }
  union(a: string, b: string) {
    const ra = this.find(a);
    const rb = this.find(b);
    if (ra !== rb) this.parent.set(rb, ra);
  }
}

function fanOutValueStack(
  group: PlotAnchor[],
  bounds: PlotBounds
): Map<string, { cx: number; cy: number }> {
  const out = new Map<string, { cx: number; cy: number }>();
  const cx = group.reduce((sum, a) => sum + a.ax, 0) / group.length;
  const cy = group.reduce((sum, a) => sum + a.ay, 0) / group.length;
  const sorted = [...group].sort((a, b) => a.id.localeCompare(b.id));
  const n = sorted.length;
  const ringR = Math.max(
    MAP_COLLISION_BASE_PX + 2,
    (MIN_CENTER_DIST * n) / (2 * Math.PI) + MAP_NODE_R * 0.5
  );

  sorted.forEach((a, idx) => {
    const angle = (2 * Math.PI * idx) / n - Math.PI / 2;
    const ring = Math.floor(idx / 8);
    const radius = ringR + ring * (MIN_CENTER_DIST * 0.85);
    out.set(a.id, {
      cx: clampNum(cx + Math.cos(angle) * radius, bounds.minX, bounds.maxX),
      cy: clampNum(cy + Math.sin(angle) * radius, bounds.minY, bounds.maxY),
    });
  });
  return out;
}

/** Gentle push when bubbles still touch after stack fan-out; stay near true coords. */
function separateTouchingBubbles(
  anchors: PlotAnchor[],
  positions: Map<string, { cx: number; cy: number }>,
  bounds: PlotBounds,
  spacing?: ModalBubbleSpacing
): Map<string, { cx: number; cy: number }> {
  const touchDist = spacing?.touchDist ?? MODAL_TOUCH_DIST;
  const targetGap = spacing?.targetGap ?? MODAL_TARGET_GAP;
  const maxDrift = spacing?.maxDrift ?? MODAL_MAX_DRIFT;
  const nodes = anchors.map(a => {
    const p = positions.get(a.id)!;
    return { id: a.id, ax: a.ax, ay: a.ay, x: p.cx, y: p.cy };
  });

  for (let iter = 0; iter < 24; iter++) {
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        let dx = nodes[j].x - nodes[i].x;
        let dy = nodes[j].y - nodes[i].y;
        let d = Math.hypot(dx, dy);
        if (d >= touchDist) continue;
        if (d < 1e-4) {
          const angle = (2 * Math.PI * ((i * 19 + j * 7) % 100)) / 100;
          dx = Math.cos(angle);
          dy = Math.sin(angle);
          d = 1;
        }
        const push = ((targetGap - d) / d) * 0.52;
        nodes[i].x -= dx * push;
        nodes[i].y -= dy * push;
        nodes[j].x += dx * push;
        nodes[j].y += dy * push;
      }
    }
  }

  const out = new Map<string, { cx: number; cy: number }>();
  nodes.forEach(n => {
    let x = n.x;
    let y = n.y;
    const drift = Math.hypot(x - n.ax, y - n.ay);
    if (drift > maxDrift) {
      const s = maxDrift / drift;
      x = n.ax + (x - n.ax) * s;
      y = n.ay + (y - n.ay) * s;
    }
    out.set(n.id, {
      cx: clampNum(x, bounds.minX, bounds.maxX),
      cy: clampNum(y, bounds.minY, bounds.maxY),
    });
  });
  return out;
}

/**
 * True metric coordinates; fan stacks that share metric values or the same
 * plot pixel; nudge apart when bubbles still touch.
 */
function resolveModalPlotPositions<T extends { account_id: string }>(
  points: T[],
  getX: (p: T) => number,
  getY: (p: T) => number,
  bounds: PlotBounds,
  getStackKey?: (p: T) => string,
  spacing?: ModalBubbleSpacing
): Map<string, { cx: number; cy: number }> {
  if (!points.length) return new Map();

  const anchors: PlotAnchor[] = points.map(p => ({
    id: p.account_id,
    ax: getX(p),
    ay: getY(p),
  }));

  let result = new Map<string, { cx: number; cy: number }>();
  anchors.forEach(a => {
    result.set(a.id, {
      cx: clampNum(a.ax, bounds.minX, bounds.maxX),
      cy: clampNum(a.ay, bounds.minY, bounds.maxY),
    });
  });

  const uf = new UnionFind();
  anchors.forEach(a => uf.parent.set(a.id, a.id));

  const linkGroup = (group: PlotAnchor[]) => {
    if (group.length <= 1) return;
    for (let i = 1; i < group.length; i++) {
      uf.union(group[0].id, group[i].id);
    }
  };

  if (getStackKey) {
    const byMetric = new Map<string, PlotAnchor[]>();
    anchors.forEach((a, idx) => {
      const key = getStackKey(points[idx]);
      const list = byMetric.get(key) ?? [];
      list.push(a);
      byMetric.set(key, list);
    });
    byMetric.forEach(linkGroup);
  }

  const byPixel = new Map<string, PlotAnchor[]>();
  anchors.forEach(a => {
    const key = `${Math.round(a.ax)}:${Math.round(a.ay)}`;
    const list = byPixel.get(key) ?? [];
    list.push(a);
    byPixel.set(key, list);
  });
  byPixel.forEach(linkGroup);

  const clusters = new Map<string, PlotAnchor[]>();
  anchors.forEach(a => {
    const root = uf.find(a.id);
    const list = clusters.get(root) ?? [];
    list.push(a);
    clusters.set(root, list);
  });

  clusters.forEach(group => {
    if (group.length <= 1) return;
    fanOutValueStack(group, bounds).forEach((pos, id) => result.set(id, pos));
  });

  result = separateTouchingBubbles(anchors, result, bounds, spacing);
  return result;
}

function scaleRevenueLayoutForModal(
  layout: RevenueScatterLayout
): RevenueScatterLayout {
  const padB =
    layout.revH - layout.padT - layout.revPlotH + MODAL_REVENUE_BOTTOM_PAD;
  const padR = layout.revW - layout.padL - layout.revPlotW;
  const revPlotH = Math.round(layout.revPlotH * MODAL_REVENUE_HEIGHT_SCALE);
  const revPlotW = Math.round(layout.revPlotW * MODAL_REVENUE_WIDTH_SCALE);
  const revH = layout.padT + revPlotH + padB;
  const revW = layout.padL + revPlotW + padR;
  const revenueYBottom = layout.padT + revPlotH;
  const revenueHLow = revPlotH * REVENUE_NRR_BREAK_FRAC;
  const revenueYJoin = revenueYBottom - revenueHLow;
  return {
    ...layout,
    revW,
    revH,
    revPlotW,
    revPlotH,
    revenueYBottom,
    revenueYJoin,
    revenueHLow,
    revenueYTop: layout.padT,
  };
}

function ScatterNode({
  point,
  cx,
  cy,
  selected,
  nodeLabel,
  showScore = true,
  onToggle,
  onHoverStart,
  onHoverMove,
  onHoverEnd,
}: {
  point: BoxSelectionPoint;
  cx: number;
  cy: number;
  selected: boolean;
  nodeLabel: string;
  /** Revenue map: initials only; reach map keeps score below initials. */
  showScore?: boolean;
  onToggle: () => void;
  onHoverStart?: (event: React.MouseEvent<SVGGElement>) => void;
  onHoverMove?: (event: React.MouseEvent<SVGGElement>) => void;
  onHoverEnd?: () => void;
}) {
  const bhThresholds = useAppSelector(
    state =>
      state.vendorAdmin.healthScoreThresholds?.business_health ??
      DEFAULT_HEALTH_SCORE_THRESHOLDS.business_health
  );
  const r = MAP_NODE_R;
  const badgeR = 5.5;
  const badgeOffset = r * 0.72;
  const overallScore = Math.round(point.overall);
  const healthDotFill = healthDotColorFromOverall(overallScore, bhThresholds);
  const nodeFill = nodeFillFromOverall(overallScore, bhThresholds);
  const bx = cx + badgeOffset;
  const by = cy - badgeOffset;

  return (
    <g
      style={{ cursor: "pointer" }}
      opacity={1}
      onMouseEnter={onHoverStart}
      onMouseMove={onHoverMove}
      onMouseLeave={onHoverEnd}
      onClick={e => {
        e.stopPropagation();
        onToggle();
      }}
      role="button"
      tabIndex={0}
      aria-label={`${point.account_name}, score ${Math.round(point.overall)}${selected ? ", selected" : ""}`}
      onKeyDown={e => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggle();
        }
      }}
    >
      {selected ? (
        <circle
          cx={cx}
          cy={cy}
          r={r + SELECTION_RING_GAP}
          fill="none"
          stroke="#7c3aed"
          strokeWidth={1.5}
          strokeDasharray="3 2"
          opacity={0.9}
          pointerEvents="none"
        />
      ) : null}
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill={nodeFill}
        stroke="#d1d5db"
        strokeWidth={1.5}
      />
      <text
        x={cx}
        y={showScore ? cy - 3 : cy}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={9.5}
        fontWeight={700}
        fill="#374151"
        pointerEvents="none"
      >
        {nodeLabel}
      </text>
      {showScore ? (
        <text
          x={cx}
          y={cy + 5}
          textAnchor="middle"
          dominantBaseline="central"
          fontSize={9}
          fontWeight={600}
          fill="#4b5563"
          pointerEvents="none"
        >
          {Math.round(point.overall)}
        </text>
      ) : null}
      <circle
        cx={bx}
        cy={by}
        r={badgeR + 0.8}
        fill="#fff"
        pointerEvents="none"
      />
      <circle
        cx={bx}
        cy={by}
        r={badgeR}
        fill={healthDotFill}
        stroke="#fff"
        strokeWidth={1.2}
        pointerEvents="none"
      >
        <title>{`${point.account_name} · score ${overallScore}`}</title>
      </circle>
    </g>
  );
}

type AccountHoverHandlers = {
  onHoverStart: (
    accountId: string,
    event: React.MouseEvent<SVGGElement>
  ) => void;
  onHoverMove: (event: React.MouseEvent<SVGGElement>) => void;
  onHoverEnd: () => void;
};

function ReachScatterPlot({
  accounts,
  layout,
  nodeLabels,
  selectedIds,
  onToggleAccount,
  accountHover,
  execAttnCaution,
  execAttnCritical,
}: {
  accounts: BoxSelectionPoint[];
  layout: ReachScatterLayout;
  nodeLabels: Map<string, string>;
  selectedIds: Set<string>;
  onToggleAccount: (id: string) => void;
  accountHover: AccountHoverHandlers;
  execAttnCaution: number;
  execAttnCritical: number;
}) {
  const scaledLayout = useMemo(
    () => scaleReachLayoutForModal(layout),
    [
      layout.rw,
      layout.rh,
      layout.padL,
      layout.padT,
      layout.plotW,
      layout.plotH,
      layout.qTopTitleY,
      layout.qTopSubY,
      layout.qBotTitleY,
      layout.qBotSubY,
    ]
  );

  const {
    rw,
    rh,
    padL,
    padT,
    plotW,
    plotH,
    hx,
    hy,
    qTopTitleY,
    qTopSubY,
    qBotTitleY,
    qBotSubY,
  } = scaledLayout;

  const npx = (p: BoxSelectionPoint) =>
    padL + (p.reachOpp * 0.88 + 0.06) * plotW;
  const npy = (p: BoxSelectionPoint) =>
    padT + (1 - (p.reachRisk * 0.88 + 0.06)) * plotH;

  const plotBounds = useMemo(
    () => ({
      minX: padL + PLOT_NODE_MARGIN,
      minY: padT + PLOT_NODE_MARGIN,
      maxX: padL + plotW - PLOT_NODE_MARGIN,
      maxY: padT + plotH - PLOT_NODE_MARGIN,
    }),
    [padL, padT, plotW, plotH]
  );

  const positions = useMemo(
    () =>
      resolveModalPlotPositions(
        accounts,
        npx,
        npy,
        plotBounds,
        p => `${Math.round(p.reachOpp * 100)}|${Math.round(p.reachRisk * 100)}`,
        {
          touchDist: MODAL_REACH_TOUCH_DIST,
          targetGap: MODAL_REACH_TARGET_GAP,
          maxDrift: MODAL_REACH_MAX_DRIFT,
        }
      ),
    [accounts, plotBounds, padL, padT, plotW, plotH]
  );

  const viewBox = `0 0 ${rw} ${rh}`;
  const axisTitleX = padL + plotW / 2;
  const xLowLabel = padL + plotW * 0.25;
  const xHighLabel = padL + plotW * 0.75;
  const frameAspect = rw / rh;

  return (
    <div
      className="mx-auto w-full max-h-[min(68vh,580px)] overflow-visible"
      style={{ aspectRatio: frameAspect }}
    >
      <svg
        viewBox={viewBox}
        className="size-full overflow-visible select-none"
        preserveAspectRatio="xMidYMid meet"
      >
        <rect
          x={padL}
          y={padT}
          width={plotW}
          height={plotH}
          fill="#fafbfc"
          rx={5}
        />
        <rect
          x={padL}
          y={padT}
          width={plotW}
          height={plotH}
          rx={5}
          fill="rgba(226,232,240,0.32)"
        />
        <line
          x1={padL}
          y1={hy}
          x2={padL + plotW}
          y2={hy}
          stroke="#e4e8ed"
          strokeWidth={1}
          strokeDasharray="5 3"
        />
        <line
          x1={hx}
          y1={padT}
          x2={hx}
          y2={padT + plotH}
          stroke="#e4e8ed"
          strokeWidth={1}
          strokeDasharray="5 3"
        />
        <line
          x1={padL}
          y1={padT + plotH}
          x2={padL}
          y2={padT + 2}
          stroke="#d1d5db"
          strokeWidth={1}
        />
        <polygon
          points={`${padL},${padT - 2} ${padL - 3},${padT + 7} ${padL + 3},${padT + 7}`}
          fill="#9ca3af"
        />
        <text
          x={13}
          y={padT + plotH / 2}
          textAnchor="middle"
          fontSize={12}
          fill="#64748b"
          fontWeight={500}
          transform={`rotate(-90,13,${padT + plotH / 2})`}
        >
          Risk
        </text>
        <text
          x={padL - 6}
          y={padT + plotH / 4}
          dominantBaseline="central"
          textAnchor="end"
          fontSize={10}
          fill="#94a3b8"
        >
          High
        </text>
        <text
          x={padL - 6}
          y={padT + (3 * plotH) / 4}
          dominantBaseline="central"
          textAnchor="end"
          fontSize={10}
          fill="#94a3b8"
        >
          Low
        </text>
        <line
          x1={padL}
          y1={padT + plotH}
          x2={padL + plotW - 2}
          y2={padT + plotH}
          stroke="#d1d5db"
          strokeWidth={1}
        />
        <polygon
          points={`${padL + plotW + 2},${padT + plotH} ${padL + plotW - 7},${padT + plotH - 3} ${padL + plotW - 7},${padT + plotH + 3}`}
          fill="#9ca3af"
        />
        <text
          x={axisTitleX}
          y={rh - 4}
          textAnchor="middle"
          fontSize={12}
          fill="#64748b"
          fontWeight={500}
        >
          Growth Opportunity
        </text>
        <text
          x={xLowLabel}
          y={padT + plotH + 14}
          textAnchor="middle"
          fontSize={10}
          fill="#94a3b8"
        >
          Low
        </text>
        <text
          x={xHighLabel}
          y={padT + plotH + 14}
          textAnchor="middle"
          fontSize={10}
          fill="#94a3b8"
        >
          High
        </text>
        <text
          x={padL + 8}
          y={qTopTitleY}
          fontSize={11}
          fontWeight={600}
          fill="#f87171"
        >
          Triage
        </text>
        <text x={padL + 8} y={qTopSubY} fontSize={9.5} fill="#94a3b8">
          Exec: Intervene
        </text>
        <text
          x={padL + plotW - 8}
          y={qTopTitleY}
          textAnchor="end"
          fontSize={11}
          fontWeight={600}
          fill="#fb923c"
        >
          Defend
        </text>
        <text
          x={padL + plotW - 8}
          y={qTopSubY}
          textAnchor="end"
          fontSize={9.5}
          fill="#94a3b8"
        >
          Exec: Protect
        </text>
        <text
          x={padL + 8}
          y={qBotTitleY}
          fontSize={11}
          fontWeight={500}
          fill="#64748b"
        >
          Monitor
        </text>
        <text x={padL + 8} y={qBotSubY} fontSize={9.5} fill="#94a3b8">
          Exec: Monitor
        </text>
        <text
          x={padL + plotW - 8}
          y={qBotTitleY}
          textAnchor="end"
          fontSize={11}
          fontWeight={600}
          fill="#22c55e"
        >
          Expand
        </text>
        <text
          x={padL + plotW - 8}
          y={qBotSubY}
          textAnchor="end"
          fontSize={9.5}
          fill="#94a3b8"
        >
          Exec: Enable
        </text>

        {accounts.map(p => {
          const pos = positions.get(p.account_id) ?? {
            cx: npx(p),
            cy: npy(p),
          };
          return (
            <ScatterNode
              key={p.account_id}
              point={p}
              cx={pos.cx}
              cy={pos.cy}
              nodeLabel={
                nodeLabels.get(p.account_id) ?? initials(p.account_name)
              }
              selected={selectedIds.has(p.account_id)}
              onToggle={() => onToggleAccount(p.account_id)}
              onHoverStart={e => accountHover.onHoverStart(p.account_id, e)}
              onHoverMove={accountHover.onHoverMove}
              onHoverEnd={accountHover.onHoverEnd}
            />
          );
        })}
      </svg>
    </div>
  );
}

function RevenueScatterPlot({
  accounts,
  layout,
  nodeLabels,
  selectedIds,
  onToggleAccount,
  accountHover,
  execAttnCaution,
  execAttnCritical,
}: {
  accounts: BoxSelectionPoint[];
  layout: RevenueScatterLayout;
  nodeLabels: Map<string, string>;
  selectedIds: Set<string>;
  onToggleAccount: (id: string) => void;
  accountHover: AccountHoverHandlers;
  execAttnCaution: number;
  execAttnCritical: number;
}) {
  const scaledLayout = useMemo(
    () => scaleRevenueLayoutForModal(layout),
    [
      layout.revW,
      layout.revH,
      layout.padL,
      layout.padT,
      layout.revPlotW,
      layout.revPlotH,
      layout.revenueYBottom,
      layout.revenueYJoin,
      layout.revenueHLow,
    ]
  );

  const {
    revW,
    revH,
    padL,
    padT,
    revPlotW,
    revPlotH,
    arrMin,
    arrMax,
    arrTicks,
    arrMedianM,
    revenueNrrMainTicks,
    revenueYBottom,
    revenueYJoin,
    revenueYTop,
    revenueHLow,
    nrrTop,
    nrrMainSpan,
  } = scaledLayout;

  const toRevX = (arrM: number) =>
    padL +
    ((clamp(arrM, arrMin, arrMax) - arrMin) / Math.max(arrMax - arrMin, 1)) *
      revPlotW;

  const toRevY = (nrr: number) => {
    const n = clamp(nrr, 0, nrrTop);
    if (n <= 80) {
      return revenueYBottom - (n / 80) * revenueHLow;
    }
    return (
      revenueYJoin - ((n - 80) / nrrMainSpan) * (revenueYJoin - revenueYTop)
    );
  };

  const plotBounds = useMemo(
    () => ({
      minX: padL + PLOT_NODE_MARGIN,
      minY: padT + PLOT_NODE_MARGIN,
      maxX: padL + revPlotW - PLOT_NODE_MARGIN,
      maxY: padT + revPlotH - PLOT_NODE_MARGIN,
    }),
    [padL, padT, revPlotW, revPlotH]
  );

  const positions = useMemo(
    () =>
      resolveModalPlotPositions(
        accounts,
        p => toRevX(p.arrM),
        p => toRevY(p.nrr),
        plotBounds,
        p => `${Math.round(p.arrM)}|${Math.round(p.nrr)}`
      ),
    [
      accounts,
      plotBounds,
      arrMin,
      arrMax,
      revPlotW,
      padL,
      nrrTop,
      nrrMainSpan,
      revenueYBottom,
      revenueYJoin,
      revenueYTop,
      revenueHLow,
    ]
  );

  const viewBox = `0 0 ${revW} ${revH}`;
  const axisTitleX = padL + revPlotW / 2;
  const frameAspect = revW / revH;

  return (
    <div
      className="mx-auto w-full max-h-[min(72vh,620px)] overflow-visible"
      style={{ aspectRatio: frameAspect }}
    >
      <svg
        viewBox={viewBox}
        className="size-full overflow-visible select-none"
        preserveAspectRatio="xMidYMid meet"
      >
        <rect
          x={padL}
          y={padT}
          width={revPlotW}
          height={revPlotH}
          fill="#f9fafb"
          rx={5}
          stroke="#e2e8f0"
          strokeWidth={1.2}
        />
        {revenueNrrMainTicks.map((nrr, idx) => {
          const y = toRevY(nrr);
          const is100 = nrr === 100;
          const isMajorDecade = idx % 2 === 0;
          const stroke = is100
            ? "#9aa8ba"
            : isMajorDecade
              ? "#edf1f6"
              : "#f1f5f9";
          const strokeW = is100 ? 1.5 : isMajorDecade ? 0.8 : 0.55;
          return (
            <g key={nrr}>
              <line
                x1={padL}
                y1={y}
                x2={padL + revPlotW}
                y2={y}
                stroke={stroke}
                strokeWidth={strokeW}
                strokeDasharray={is100 ? "7 4" : "none"}
              />
              <text
                x={padL - 5}
                y={y}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize={11}
                fill={is100 ? "#7b8a9c" : "#cbd5e1"}
              >
                {nrr}%
              </text>
            </g>
          );
        })}
        <line
          x1={padL}
          y1={revenueYBottom}
          x2={padL + revPlotW}
          y2={revenueYBottom}
          stroke="#e2e8f0"
          strokeWidth={0.9}
        />
        <path
          d={`M ${padL - 1} ${revenueYBottom - 4} l 5,-4 M ${padL - 1} ${revenueYBottom - 12} l 5,-4`}
          stroke="#c0c8d4"
          strokeWidth={0.9}
          fill="none"
        />
        {arrTicks.map(arr => {
          const x = toRevX(arr);
          return (
            <g key={arr}>
              <line
                x1={x}
                y1={padT}
                x2={x}
                y2={padT + revPlotH}
                stroke="#edf1f6"
                strokeWidth={0.8}
              />
              <text
                x={x}
                y={padT + revPlotH + 16}
                textAnchor="middle"
                fontSize={11}
                fill="#cbd5e1"
              >
                ${arr}M
              </text>
            </g>
          );
        })}
        <line
          x1={padL}
          y1={toRevY(100)}
          x2={padL + revPlotW}
          y2={toRevY(100)}
          stroke="#c0c8d4"
          strokeWidth={1}
          strokeDasharray="4 4"
        />
        <text
          x={padL + 6}
          y={toRevY(100) - 5}
          fontSize={12}
          fontWeight={500}
          fill="#475569"
        >
          NRR = 100% (expansion boundary)
        </text>
        <line
          x1={toRevX(arrMedianM)}
          y1={padT}
          x2={toRevX(arrMedianM)}
          y2={padT + revPlotH}
          stroke="#c0c8d4"
          strokeWidth={1}
          strokeDasharray="4 4"
        />
        <text
          x={toRevX(arrMedianM)}
          y={padT - 8}
          textAnchor="middle"
          fontSize={12}
          fontWeight={500}
          fill="#475569"
        >
          Portfolio median (${arrMedianM.toFixed(1)}M)
        </text>
        <text
          x={axisTitleX}
          y={revH - 8}
          textAnchor="middle"
          fontSize={12}
          fontWeight={500}
          fill="#475569"
        >
          ARR ($M)
        </text>
        <text
          x={14}
          y={padT + revPlotH / 2}
          textAnchor="middle"
          fontSize={12}
          fontWeight={500}
          fill="#475569"
          transform={`rotate(-90,14,${padT + revPlotH / 2})`}
        >
          NRR (%)
        </text>

        {accounts.map(p => {
          const pos = positions.get(p.account_id) ?? {
            cx: toRevX(p.arrM),
            cy: toRevY(p.nrr),
          };
          return (
            <ScatterNode
              key={p.account_id}
              point={p}
              cx={pos.cx}
              cy={pos.cy}
              nodeLabel={
                nodeLabels.get(p.account_id) ?? initials(p.account_name)
              }
              selected={selectedIds.has(p.account_id)}
              showScore={false}
              onToggle={() => onToggleAccount(p.account_id)}
              onHoverStart={e => accountHover.onHoverStart(p.account_id, e)}
              onHoverMove={accountHover.onHoverMove}
              onHoverEnd={accountHover.onHoverEnd}
            />
          );
        })}
      </svg>
    </div>
  );
}

const BirdsEyeBoxSelectionModal: React.FC<BirdsEyeBoxSelectionModalProps> = ({
  open,
  onOpenChange,
  view,
  accountIds,
  pointsById,
  selectedIds,
  onToggleAccount,
  execAttnCaution,
  execAttnCritical,
  reachLayout,
  revenueLayout,
  onSaveWatchlist,
  onSwitchMapView,
  compareAccounts = [],
  compareCategoryOrder = [],
  compareColors = [],
}) => {
  const [activePanel, setActivePanel] = useState<
    "reach" | "revenue" | "compare"
  >(view);
  const [compareActiveIds, setCompareActiveIds] = useState<string[]>([]);
  const [compareValidationMessage, setCompareValidationMessage] = useState<
    string | null
  >(null);
  const [hoverAccountId, setHoverAccountId] = useState<string | null>(null);
  const [hoverPos, setHoverPos] = useState({ x: 0, y: 0 });
  const hoverLeaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHoverLeaveTimer = useCallback(() => {
    if (hoverLeaveTimerRef.current) {
      clearTimeout(hoverLeaveTimerRef.current);
      hoverLeaveTimerRef.current = null;
    }
  }, []);

  const scheduleHoverHide = useCallback(() => {
    clearHoverLeaveTimer();
    hoverLeaveTimerRef.current = setTimeout(() => {
      setHoverAccountId(null);
      hoverLeaveTimerRef.current = null;
    }, 140);
  }, [clearHoverLeaveTimer]);

  useEffect(() => () => clearHoverLeaveTimer(), [clearHoverLeaveTimer]);

  useEffect(() => {
    if (!open) {
      setHoverAccountId(null);
      clearHoverLeaveTimer();
    }
  }, [open, clearHoverLeaveTimer]);

  const accountHover = useMemo<AccountHoverHandlers>(
    () => ({
      onHoverStart: (accountId, event) => {
        clearHoverLeaveTimer();
        setHoverAccountId(accountId);
        setHoverPos({ x: event.clientX, y: event.clientY });
      },
      onHoverMove: event => {
        setHoverPos({ x: event.clientX, y: event.clientY });
      },
      onHoverEnd: scheduleHoverHide,
    }),
    [clearHoverLeaveTimer, scheduleHoverHide]
  );

  const hoverPoint = useMemo(
    () => (hoverAccountId ? (pointsById.get(hoverAccountId) ?? null) : null),
    [hoverAccountId, pointsById]
  );

  const hoverFixedPosition = useMemo(() => {
    if (!hoverPoint) return null;
    const height = activePanel === "revenue" || view === "revenue" ? 168 : 220;
    return computeMapHoverPosition(hoverPos.x, hoverPos.y, height);
  }, [hoverPoint, hoverPos.x, hoverPos.y, activePanel, view]);

  useEffect(() => {
    if (open) {
      setActivePanel(view);
    }
  }, [open, view]);

  const accounts = useMemo(() => {
    const seen = new Set<string>();
    return accountIds
      .map(id => pointsById.get(id))
      .filter((p): p is BoxSelectionPoint => {
        if (!p || seen.has(p.account_id)) return false;
        seen.add(p.account_id);
        return true;
      });
  }, [accountIds, pointsById]);

  const nodeLabels = useMemo(() => buildMapNodeLabels(accounts), [accounts]);

  const selectedAccounts = useMemo(
    () => accounts.filter(a => selectedIds.has(a.account_id)),
    [accounts, selectedIds]
  );
  const selectedCount = selectedAccounts.length;
  const compareAccountIds = useMemo(
    () => new Set(compareAccounts.map(account => account.account_id)),
    [compareAccounts]
  );
  const compareAccountsInSelection = useMemo(() => {
    if (!compareAccounts.length) return [];
    const selectionAccountIdSet = new Set(accountIds);
    return compareAccounts.filter(account =>
      selectionAccountIdSet.has(account.account_id)
    );
  }, [accountIds, compareAccounts]);
  const defaultCompareActiveIds = useMemo(
    () =>
      accountIds
        .filter(
          accountId =>
            selectedIds.has(accountId) && compareAccountIds.has(accountId)
        )
        .slice(0, MAX_COMPARE_VISIBLE_ACCOUNTS),
    [accountIds, compareAccountIds, selectedIds]
  );
  useEffect(() => {
    const availableIds = new Set(
      compareAccountsInSelection.map(account => account.account_id)
    );
    setCompareActiveIds(prev => {
      const kept = prev
        .filter(accountId => availableIds.has(accountId))
        .slice(0, MAX_COMPARE_VISIBLE_ACCOUNTS);
      if (open && kept.length > 0) {
        return kept;
      }
      return defaultCompareActiveIds;
    });
    setCompareValidationMessage(null);
  }, [compareAccountsInSelection, defaultCompareActiveIds, open]);

  const toggleCompareAccount = useCallback((accountId: string) => {
    setCompareActiveIds(prev => {
      if (prev.includes(accountId)) {
        if (prev.length <= 1) {
          setCompareValidationMessage(
            "At least 1 account should remain active in the compare chart."
          );
          return prev;
        }
        setCompareValidationMessage(null);
        return prev.filter(id => id !== accountId);
      }
      if (prev.length >= MAX_COMPARE_VISIBLE_ACCOUNTS) {
        setCompareValidationMessage(
          "Only 4 accounts can be shown at a time. Deselect one active account to select another."
        );
        return prev;
      }
      setCompareValidationMessage(null);
      return [...prev, accountId];
    });
  }, []);

  const showCompareTab =
    compareAccountsInSelection.length > 0 && compareCategoryOrder.length > 0;
  const canCompareInModal = showCompareTab && compareActiveIds.length >= 1;
  const modalTabs = useMemo(
    () => [
      {
        id: "reach",
        label: "Risk-Opportunity Map",
        icon: <Radar className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />,
      },
      {
        id: "revenue",
        label: "Revenue Map",
        icon: <TrendingUp className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />,
      },
      ...(showCompareTab
        ? [
            {
              id: "compare",
              label: "Compare Accounts",
              icon: <Scale className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />,
            },
          ]
        : []),
    ],
    [canCompareInModal, showCompareTab]
  );
  const ViewIcon =
    activePanel === "compare" ? Scale : view === "reach" ? Radar : TrendingUp;
  const handleClose = () => onOpenChange(false);
  const helperText =
    activePanel === "compare"
      ? compareAccountsInSelection.length > MAX_COMPARE_VISIBLE_ACCOUNTS
        ? `Showing ${compareActiveIds.length} of ${compareAccountsInSelection.length} available accounts in the compare chart. Deselect one active account to select another.`
        : `${compareActiveIds.length} active account${compareActiveIds.length === 1 ? "" : "s"} shown in the compare chart · up to 4 accounts can be plotted at a time`
      : view === "reach"
        ? `${accounts.length} accounts in this area · spread within the same quadrant so every account is visible · all pre-selected`
        : `${accounts.length} accounts in this area · aligned to values · fan on same ARR/NRR · small gap when touching · all pre-selected`;

  return (
    <CommonModal
      isOpen={open}
      toggle={handleClose}
      title="Snippet Account Selection"
      size="6xl"
      className="min-h-[96vh] w-[98vw] max-w-[1120px] rounded-[14px] border border-slate-200 shadow-2xl"
      contentClassName="overflow-x-hidden overflow-y-auto p-0"
      footer={
        <div className="flex flex-wrap items-center justify-between gap-2 bg-slate-50/60 px-1 py-0">
          <span className="text-[12px] font-medium text-slate-600">
            {selectedCount} of {accounts.length} selected
          </span>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {onSaveWatchlist ? (
              <button
                type="button"
                onClick={onSaveWatchlist}
                disabled={selectedCount === 0}
                className={MODAL_OUTLINE_BTN}
                title="Save selected accounts as a watchlist"
              >
                <Bookmark className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
                Save as watchlist
              </button>
            ) : null}
          </div>
        </div>
      }
    >
      <p className="mb-0 mt-0 flex items-start gap-1.5 px-4 pt-1 text-[12px] leading-snug text-slate-500">
        <ViewIcon
          className="mt-0.5 h-4 w-4 shrink-0 text-violet-600"
          strokeWidth={1.75}
          aria-hidden
        />
        <span>{helperText}</span>
      </p>
      <div className="px-4 pb-1 pt-3">
        <TabsComponent
          tabs={modalTabs}
          activeTab={activePanel}
          onTabChange={(id: "reach" | "revenue" | "compare") => {
            setActivePanel(id);
            if (id === "reach" && view !== "reach") {
              onSwitchMapView?.();
            }
            if (id === "revenue" && view !== "revenue") {
              onSwitchMapView?.();
            }
          }}
          variant="tab-segmented"
          className="mb-0 max-w-full"
        />
      </div>
      <div className="overflow-visible px-2 py-3">
        {activePanel === "compare" ? (
          <SpiderCompareModalContent
            embedded
            allAccounts={compareAccountsInSelection}
            selectedAccountIds={compareActiveIds}
            categoryOrder={compareCategoryOrder}
            compareColors={compareColors}
            onToggleAccount={toggleCompareAccount}
            onClose={handleClose}
            validationMessage={compareValidationMessage}
          />
        ) : activePanel === "reach" ? (
          <ReachScatterPlot
            accounts={accounts}
            layout={reachLayout}
            nodeLabels={nodeLabels}
            selectedIds={selectedIds}
            onToggleAccount={onToggleAccount}
            accountHover={accountHover}
            execAttnCaution={execAttnCaution}
            execAttnCritical={execAttnCritical}
          />
        ) : activePanel === "revenue" ? (
          <RevenueScatterPlot
            accounts={accounts}
            layout={revenueLayout}
            nodeLabels={nodeLabels}
            selectedIds={selectedIds}
            onToggleAccount={onToggleAccount}
            accountHover={accountHover}
            execAttnCaution={execAttnCaution}
            execAttnCritical={execAttnCritical}
          />
        ) : null}
      </div>

      {hoverPoint && hoverFixedPosition && activePanel !== "compare" ? (
        <div
          style={{
            position: "fixed",
            left: hoverFixedPosition.left,
            top: hoverFixedPosition.top,
            zIndex: 70,
          }}
          onMouseEnter={clearHoverLeaveTimer}
          onMouseLeave={scheduleHoverHide}
        >
          {activePanel === "reach" ? (
            <ReachMapAccountHoverCard
              point={hoverPoint}
              execAttnCaution={execAttnCaution}
              execAttnCritical={execAttnCritical}
            />
          ) : (
            <RevenueMapAccountHoverCard point={hoverPoint} />
          )}
        </div>
      ) : null}
    </CommonModal>
  );
};

export default BirdsEyeBoxSelectionModal;
